
import React, { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from '@/components/ui/use-toast';
import { Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface ProfilePictureUploadProps {
  size?: 'sm' | 'md' | 'lg';
  editable?: boolean;
  onUploadComplete?: (url: string) => void;
  avatarUrl?: string | null;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({ 
  size = 'md', 
  editable = true,
  onUploadComplete,
  avatarUrl: initialAvatarUrl 
}) => {
  const { user, userProfile } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialAvatarUrl || userProfile?.avatar_url || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-16 h-16',
    lg: 'w-24 h-24'
  };

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      setUploading(true);

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (urlError) {
        throw urlError;
      }

      // Update profile with new avatar URL
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user?.id);

      if (profileUpdateError) {
        throw profileUpdateError;
      }

      setAvatarUrl(publicUrl);
      onUploadComplete?.(publicUrl);

      toast({
        title: 'Profile Picture Updated',
        description: 'Your profile picture has been successfully uploaded.',
      });
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    try {
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user?.id);

      if (profileUpdateError) {
        throw profileUpdateError;
      }

      setAvatarUrl(null);
      onUploadComplete?.(null);

      toast({
        title: 'Profile Picture Removed',
        description: 'Your profile picture has been removed.',
      });
    } catch (error) {
      toast({
        title: 'Remove Failed',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive'
      });
    }
  };

  const renderEditButtons = () => {
    if (!editable) return null;

    return (
      <div className="absolute bottom-0 right-0 flex space-x-1">
        <Button 
          type="button" 
          size="icon" 
          variant="outline" 
          className="rounded-full w-6 h-6"
          onClick={() => fileInputRef.current?.click()}
        >
          <Pencil className="h-3 w-3" />
        </Button>
        {avatarUrl && (
          <Button 
            type="button" 
            size="icon" 
            variant="destructive" 
            className="rounded-full w-6 h-6"
            onClick={removeAvatar}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="relative inline-block">
      <Avatar className={`${sizeClasses[size]} ${editable ? 'hover:opacity-80 transition-opacity' : ''}`}>
        <AvatarImage 
          src={avatarUrl || undefined} 
          alt={`${userProfile?.display_name || 'User'}'s avatar`} 
        />
        <AvatarFallback>
          {userProfile?.display_name ? userProfile.display_name.substring(0, 2).toUpperCase() : 'UN'}
        </AvatarFallback>
      </Avatar>
      {renderEditButtons()}
      <input 
        type="file" 
        ref={fileInputRef}
        accept="image/*"
        onChange={uploadAvatar}
        className="hidden"
        disabled={uploading}
      />
    </div>
  );
};

export default ProfilePictureUpload;
